import React, { useEffect, useMemo, useState } from "react";
import { api, formatINR, toISO, todayISO } from "@/lib/api";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_DEPOSIT = 370; // Fixed syndicate deposit on run days

const emptyForm = {
  is_halt: false,
  sales: "",
  deposit: DEFAULT_DEPOSIT,
  net_earnings: "",
  extra_income: "",
  extra_income_remark: "",
  expenses: [],
  expense_remark: "",
  savings_credit: "",
  savings_debit: "",
  savings_remark: "",
  remark: "",
};

export default function DailyEntry() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");
  const [date, setDate] = useState(new Date());
  const [form, setForm] = useState(emptyForm);
  const [monthEntries, setMonthEntries] = useState([]);

  useEffect(() => {
    api.get("/vehicles").then((r) => {
      setVehicles(r.data);
      if (r.data.length && !vehicleId) setVehicleId(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthBounds = useMemo(() => {
    const d = date || new Date();
    const start = new Date(d.getFullYear(), d.getMonth(), 1);
    const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { start: toISO(start), end: toISO(end) };
  }, [date]);

  // load month entries for calendar markers
  useEffect(() => {
    if (!vehicleId) return;
    api
      .get("/entries", {
        params: { vehicle_id: vehicleId, start: monthBounds.start, end: monthBounds.end },
      })
      .then((r) => setMonthEntries(r.data));
  }, [vehicleId, monthBounds.start, monthBounds.end]);

  // load entry for selected date
  useEffect(() => {
    if (!vehicleId) return;
    const d = toISO(date);
    const existing = monthEntries.find((e) => e.date === d);
    if (existing) {
      setForm({
        is_halt: existing.is_halt || false,
        sales: existing.sales || "",
        deposit:
          existing.deposit !== undefined && existing.deposit !== null
            ? existing.deposit
            : "",
        net_earnings: existing.net_earnings || "",
        extra_income: existing.extra_income || "",
        extra_income_remark: existing.extra_income_remark || "",
        expenses: existing.expenses || [],
        expense_remark: existing.expense_remark || "",
        savings_credit: existing.savings_credit || "",
        savings_debit: existing.savings_debit || "",
        savings_remark: existing.savings_remark || "",
        remark: existing.remark || "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [date, vehicleId, monthEntries]);

  // Toggle deposit default when halt switches
  const setHalt = (v) => {
    setForm((f) => ({
      ...f,
      is_halt: v,
      deposit: v ? 0 : f.deposit === 0 || f.deposit === "" ? DEFAULT_DEPOSIT : f.deposit,
    }));
  };

  const datesWithEntry = useMemo(
    () => monthEntries.filter((e) => !e.is_halt).map((e) => new Date(e.date)),
    [monthEntries]
  );
  const haltDates = useMemo(
    () => monthEntries.filter((e) => e.is_halt).map((e) => new Date(e.date)),
    [monthEntries]
  );

  const num = (v) => (v === "" || v === null ? 0 : Number(v) || 0);

  const onSave = async () => {
    if (!vehicleId) {
      toast.error("Select a vehicle");
      return;
    }
    const payload = {
      vehicle_id: vehicleId,
      date: toISO(date),
      is_halt: form.is_halt,
      sales: num(form.sales),
      deposit: num(form.deposit),
      net_earnings: num(form.net_earnings),
      extra_income: num(form.extra_income),
      extra_income_remark: form.extra_income_remark || "",
      expenses: (form.expenses || []).map((e) => ({
        amount: num(e.amount),
        remark: e.remark || "",
      })),
      expense_remark: form.expense_remark || "",
      savings_credit: num(form.savings_credit),
      savings_debit: num(form.savings_debit),
      savings_remark: form.savings_remark || "",
      remark: form.remark || "",
    };
    try {
      await api.post("/entries", payload);
      toast.success(`Entry saved for ${toISO(date)}`);
      const r = await api.get("/entries", {
        params: { vehicle_id: vehicleId, start: monthBounds.start, end: monthBounds.end },
      });
      setMonthEntries(r.data);
    } catch (e) {
      toast.error("Failed to save");
    }
  };

  const addExpense = () =>
    setForm({ ...form, expenses: [...form.expenses, { amount: "", remark: "" }] });

  const updateExpense = (i, key, val) => {
    const copy = [...form.expenses];
    copy[i] = { ...copy[i], [key]: val };
    setForm({ ...form, expenses: copy });
  };

  const removeExpense = (i) =>
    setForm({ ...form, expenses: form.expenses.filter((_, idx) => idx !== i) });

  const expensesTotal = (form.expenses || []).reduce(
    (a, b) => a + num(b.amount),
    0
  );

  return (
    <div className="space-y-6" data-testid="daily-entry-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="label-eyebrow">Operations</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mt-1">
            Daily Entry
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pick a date and record sales, deposit, earnings & expenses.
          </p>
        </div>
        <div className="w-full md:w-[280px]">
          <Label className="label-eyebrow">Vehicle</Label>
          <Select value={vehicleId} onValueChange={setVehicleId}>
            <SelectTrigger data-testid="entry-vehicle-select" className="mt-1">
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => (
                <SelectItem key={v.id} value={v.id}>
                  {v.name} · {v.registration_number}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!vehicleId ? (
        <div className="card-swiss p-12 text-center text-gray-500">
          Add a vehicle first from the Vehicles page.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          <div className="card-swiss p-4" data-testid="entry-calendar-card">
            <div className="label-eyebrow mb-2">Calendar</div>
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              modifiers={{ hasEntry: datesWithEntry, halt: haltDates }}
              modifiersClassNames={{ hasEntry: "has-entry", halt: "has-entry is-halt" }}
              className="rounded-md"
            />
            <div className="mt-3 px-2 flex items-center gap-4 text-xs text-gray-500">
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-emerald-600" /> Entry
              </span>
              <span className="flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full bg-amber-600" /> Halt
              </span>
            </div>
          </div>

          <div className="card-swiss p-5 space-y-5" data-testid="entry-form-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="label-eyebrow">Date</div>
                <div className="font-display text-2xl font-bold tracking-tight mt-1">
                  {toISO(date)}
                </div>
              </div>
              <div className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-md">
                <Label htmlFor="halt-switch" className="text-sm">
                  Halt / Off Day
                </Label>
                <Switch
                  id="halt-switch"
                  data-testid="halt-switch"
                  checked={form.is_halt}
                  onCheckedChange={setHalt}
                />
              </div>
            </div>

            <div className={form.is_halt ? "opacity-50 pointer-events-none" : ""}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>Daily Sales (₹)</Label>
                  <Input
                    type="number"
                    data-testid="sales-input"
                    value={form.sales}
                    onChange={(e) => setForm({ ...form, sales: e.target.value })}
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Syndicate Deposit (₹)</Label>
                  <Input
                    type="number"
                    data-testid="deposit-input"
                    value={form.deposit}
                    onChange={(e) => setForm({ ...form, deposit: e.target.value })}
                    placeholder="0"
                  />
                  <p className="text-[10px] text-gray-500 mt-1">
                    Default ₹{DEFAULT_DEPOSIT} on run days · editable
                  </p>
                </div>
                <div>
                  <Label>Net Earnings (₹)</Label>
                  <Input
                    type="number"
                    data-testid="earnings-input"
                    value={form.net_earnings}
                    onChange={(e) => setForm({ ...form, net_earnings: e.target.value })}
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Expenses</Label>
                <div className="flex items-center gap-2">
                  {form.is_halt && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-2 py-0.5 rounded-sm">
                      Editable on halt days
                    </span>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addExpense}
                    data-testid="add-expense-btn"
                    className="gap-1.5"
                  >
                    <Plus className="h-3.5 w-3.5" /> Add expense
                  </Button>
                </div>
              </div>
              <div className="mt-2 space-y-2">
                {form.expenses.length === 0 && (
                  <p className="text-xs text-gray-500">
                    No expenses for this day. Click "Add expense" to log one.
                  </p>
                )}
                {form.expenses.map((e, i) => (
                  <div
                    key={i}
                    className="border border-gray-200 rounded-md p-2.5 grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2 items-center"
                    data-testid={`expense-row-${i}`}
                  >
                    <Input
                      type="number"
                      placeholder="₹ Amount"
                      value={e.amount}
                      onChange={(ev) => updateExpense(i, "amount", ev.target.value)}
                      className="font-mono"
                    />
                    <Input
                      placeholder="Remark (e.g., body emi, diesel, repair)"
                      value={e.remark}
                      onChange={(ev) => updateExpense(i, "remark", ev.target.value)}
                      className="w-full"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={() => removeExpense(i)}
                      data-testid={`remove-expense-${i}`}
                      className="justify-self-end sm:justify-self-auto"
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-500 flex justify-between">
                <span>Total expenses</span>
                <span className="font-mono font-semibold text-black">
                  {formatINR(expensesTotal)}
                </span>
              </div>
            </div>

            <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3">
              <div className="flex items-center justify-between">
                <Label className="text-emerald-800">
                  Extra Income (₹) — optional
                </Label>
                <div className="flex items-center gap-2">
                  {form.is_halt && (
                    <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-2 py-0.5 rounded-sm">
                      Editable on halt days
                    </span>
                  )}
                  <span className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                    Adds to profit
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-2 mt-2">
                <Input
                  type="number"
                  data-testid="extra-income-input"
                  value={form.extra_income}
                  onChange={(e) =>
                    setForm({ ...form, extra_income: e.target.value })
                  }
                  placeholder="0"
                />
                <Input
                  data-testid="extra-income-remark-input"
                  value={form.extra_income_remark}
                  onChange={(e) =>
                    setForm({ ...form, extra_income_remark: e.target.value })
                  }
                  placeholder="Source / remark (e.g., parcel, advertising)"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 pt-5">
              <div className="flex items-center justify-between">
                <Label className="label-eyebrow">Savings Fund</Label>
                {form.is_halt && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-700 font-bold bg-amber-100 px-2 py-0.5 rounded-sm">
                    Editable on halt days
                  </span>
                )}
              </div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Savings Credit (₹)</Label>
                  <Input
                    type="number"
                    data-testid="savings-credit-input"
                    value={form.savings_credit}
                    onChange={(e) =>
                      setForm({ ...form, savings_credit: e.target.value })
                    }
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label>Savings Debit (₹)</Label>
                  <Input
                    type="number"
                    data-testid="savings-debit-input"
                    value={form.savings_debit}
                    onChange={(e) =>
                      setForm({ ...form, savings_debit: e.target.value })
                    }
                    placeholder="0"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Savings Remark</Label>
                  <Input
                    data-testid="savings-remark-input"
                    value={form.savings_remark}
                    onChange={(e) =>
                      setForm({ ...form, savings_remark: e.target.value })
                    }
                    placeholder="Optional note for savings movement"
                  />
                </div>
              </div>
            </div>

            <div>
              <Label>Day remarks</Label>
              <Textarea
                data-testid="remark-input"
                rows={2}
                value={form.remark}
                onChange={(e) => setForm({ ...form, remark: e.target.value })}
                placeholder="Anything important about this day"
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-gray-100">
              <div className="grid grid-cols-3 gap-3 sm:gap-6 flex-1">
                <div>
                  <div className="label-eyebrow">Total Income</div>
                  <div className="font-mono font-bold text-emerald-700 mt-1">
                    {formatINR(num(form.net_earnings) + num(form.extra_income))}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Expenses</div>
                  <div className="font-mono font-bold text-red-600 mt-1">
                    {formatINR(expensesTotal)}
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow">Day Profit</div>
                  <div
                    className={`font-mono font-bold mt-1 ${
                      num(form.net_earnings) + num(form.extra_income) - expensesTotal >= 0
                        ? "text-emerald-700"
                        : "text-red-600"
                    }`}
                    data-testid="day-profit-preview"
                  >
                    {formatINR(
                      num(form.net_earnings) + num(form.extra_income) - expensesTotal
                    )}
                  </div>
                </div>
              </div>
              <Button onClick={onSave} className="gap-2" data-testid="save-entry-btn">
                <Save className="h-4 w-4" /> Save Entry
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
